import { jsx, jsxs } from 'react/jsx-runtime';
import { ArrowDownIcon } from '@radix-ui/react-icons';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { useState, useEffect } from 'react';
import { styled, css } from 'styled-components';
import { Button } from '@fabric-app/ui/components/Button';
import { DropdownMenu } from '@fabric-app/ui/components/DropdownMenu/DropdownMenu';
import { Flex } from '@fabric-app/ui/components/Flex';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { CellContent } from './cellMap.js';
import { columnTitleMap } from './columnTitleMap.js';
import { RESOURCE_TABLE_COLUMN_IDS } from '@fabric-app/types/resources/resourceListViewPreferences';
import { useResourceListPreferencesContext } from '../contexts/resourceListContextPreferences.js';
import { isSortField } from '../../../utils/queryOptionGuards.js';
import { IconVoiceNoteCheck, IconSettings } from '@fabric-app/icons/svg';

const selectableColumns = RESOURCE_TABLE_COLUMN_IDS.filter((id) => id !== "preview" && id !== "name");
const SettingsButton = styled(Button).attrs({
  size: "xss",
  variant: "bg-tertiary"
}).withConfig({
  displayName: "cellMapHeader__SettingsButton",
  componentId: "sc-cf47adc2-0"
})([
  `width:40px;height:27px;border:1px solid `,
  `;svg{height:1rem;}`
], cssVar["color-border-primary"]);
const HeaderTitle = styled.span.withConfig({
  displayName: "cellMapHeader__HeaderTitle",
  componentId: "sc-cf47adc2-1"
})([
  `font-weight:600;font-size:11px;color:`,
  `;`
], cssVar["color-text-primary"]);
const HeaderDragPlaceholder = ({ offset = 0 }) => {
  const placeholderOffsetRaw = useMotionValue(0);
  const placeholderOffset = useTransform(placeholderOffsetRaw, (value) => Math.round(value));
  const placeholderOpacity = useMotionValue(0);
  useEffect(() => {
    const controls = animate(placeholderOffsetRaw, offset || 0, {
      duration: 0.1,
      ease: "easeOut"
    });
    const opacity = animate(placeholderOpacity, offset ? 1 : 0, {
      duration: 0.05,
      ease: "easeOut"
    });
    return () => {
      controls.stop();
      opacity.stop();
    };
  }, [
    placeholderOffsetRaw,
    offset,
    placeholderOpacity
  ]);
  return /* @__PURE__ */ jsx(DragPlaceholder, {
    style: {
      left: placeholderOffset,
      opacity: placeholderOpacity
    }
  });
};
const createHeaderComponent = ({ title, Icon, disableReorder, ...rest }) => {
  return function HeaderComponentBase({ args, colId }) {
    return /* @__PURE__ */ jsx(HeaderCellContent, {
      title,
      colId,
      icon: Icon && /* @__PURE__ */ jsx(Icon, {}),
      disableReorder,
      ...args,
      ...rest
    });
  };
};
const SortButton = styled(Button).attrs((props) => {
  return {
    ...props,
    size: "xs",
    shape: "square",
    variant: "bg-tertiary"
  };
}).withConfig({
  displayName: "cellMapHeader__SortButton",
  componentId: "sc-cf47adc2-2"
})([
  `margin-right:-4px;`,
  ``
], ({ isActive }) => isActive && css([
  `opacity:1 !important;`
]));
const HeaderCellOrderButton = ({ property }) => {
  const { disabledSortFields, sort, setSort, disableSort } = useResourceListPreferencesContext();
  const isDisabled = disableSort || disabledSortFields?.includes(property);
  if (isDisabled) {
    return null;
  }
  const isActive = sort.property === property;
  const direction = sort.direction || "ASC";
  return /* @__PURE__ */ jsx(SortButton, {
    isActive,
    onClick: () => {
      setSort(property, {
        order: !isActive ? direction : direction === "ASC" ? "DESC" : "ASC"
      });
    },
    children: /* @__PURE__ */ jsx(ArrowDownIcon, {
      style: {
        ...direction === "ASC" ? {
          transform: "rotate(180deg)"
        } : void 0,
        transition: ".2s transform"
      }
    })
  });
};
const StyledCellContent = styled(CellContent).withConfig({
  displayName: "cellMapHeader__StyledCellContent",
  componentId: "sc-cf47adc2-3"
})([
  ``,
  `{opacity:0;}&:hover{`,
  `{opacity:1;}}`
], SortButton, SortButton);
const HeaderCellContent = ({ icon, title, setHighlightedHandleId, colId, highlightedHandleId, disableReorder, toggleColumn, isColumnIdVisible, ...rest }) => {
  const [pan, setPan] = useState(null);
  const { placeColumnAfter } = useResourceListPreferencesContext();
  const { disabledSortFields } = useResourceListPreferencesContext();
  return /* @__PURE__ */ jsxs(StyledCellContent, {
    justifyContent: "space-between",
    as: motion.div,
    style: disableReorder ? void 0 : {
      cursor: "grab"
    },
    ...rest,
    onPanEnd: disableReorder ? void 0 : () => {
      setPan(null);
      if (highlightedHandleId && highlightedHandleId !== colId) {
        placeColumnAfter(colId, highlightedHandleId);
      }
      setHighlightedHandleId(null);
    },
    onPan: disableReorder ? void 0 : (e, info) => {
      setPan(info);
      const pointX = info.point.x;
      const resizeHandles = document.querySelectorAll("[data-vdt-resize-handle-id]");
      if (resizeHandles.length === 0) {
        return;
      }
      let closestDistance = Infinity;
      resizeHandles.forEach((handleEl) => {
        const rect = handleEl.getBoundingClientRect();
        const distance = Math.abs(rect.left - pointX);
        if (distance < closestDistance) {
          closestDistance = distance;
          setHighlightedHandleId(handleEl.getAttribute("data-vdt-resize-handle-id") || null);
        }
      });
    },
    children: [
      /* @__PURE__ */ jsx(HeaderDragPlaceholder, {
        offset: pan?.offset.x
      }),
      /* @__PURE__ */ jsxs(Flex, {
        gap: "element",
        alignItems: "center",
        children: [
          icon,
          /* @__PURE__ */ jsx(HeaderTitle, {
            children: title
          })
        ]
      }),
      isSortField(colId) && !disabledSortFields?.includes(colId) && /* @__PURE__ */ jsx(HeaderCellOrderButton, {
        property: colId
      })
    ]
  });
};
const DragPlaceholder = styled(motion.div).withConfig({
  displayName: "cellMapHeader__DragPlaceholder",
  componentId: "sc-cf47adc2-4"
})([
  `height:100vh;width:100%;background:rgba(`,
  `,0.04);position:absolute;top:0;left:0;z-index:10;pointer-events:none;`
], cssVar["color-bg-primary-reverse-rgb"]);
const cellHeaderMap = {
  name: createHeaderComponent({
    title: columnTitleMap.name,
    disableReorder: true
  }),
  createdAt: createHeaderComponent({
    title: columnTitleMap.createdAt
  }),
  modifiedAt: createHeaderComponent({
    title: columnTitleMap.modifiedAt
  }),
  comments: createHeaderComponent({
    title: columnTitleMap.comments
  }),
  kind: createHeaderComponent({
    title: columnTitleMap.kind
  }),
  preview: ({ args }) => /* @__PURE__ */ jsx(CellContent, {
    justifyContent: "center",
    children: /* @__PURE__ */ jsx(DropdownMenu.Base, {
      renderTriggerElement: (props) => /* @__PURE__ */ jsx(SettingsButton, {
        as: DropdownMenu.Trigger,
        ...props,
        children: /* @__PURE__ */ jsx(IconSettings, {})
      }),
      contentProps: {
        align: "start"
      },
      children: selectableColumns.map((colId) => {
        return /* @__PURE__ */ jsxs(DropdownMenu.Item, {
          onSelect: (e) => {
            e.preventDefault();
            args.toggleColumn(colId);
          },
          children: [
            /* @__PURE__ */ jsx(DropdownMenu.IconContainer, {
              nonMobileVisible: true,
              children: args.isColumnIdVisible(colId) && /* @__PURE__ */ jsx(IconVoiceNoteCheck, {
                style: {
                  width: "1rem"
                }
              })
            }),
            columnTitleMap[colId]
          ]
        }, colId);
      })
    })
  }),
  tags: createHeaderComponent({
    title: columnTitleMap.tags
  }),
  size: createHeaderComponent({
    title: columnTitleMap.size,
    justifyContent: "flex-end"
  }),
  url: createHeaderComponent({
    title: columnTitleMap.url
  }),
  createdBy: createHeaderComponent({
    title: columnTitleMap.createdBy
  })
};

export { cellHeaderMap, selectableColumns };
//# sourceMappingURL=cellMapHeader.js.map
